/**
 * The **pack SDK**: how a content pack declares its entries in TypeScript. A pack's `pack.ts`
 * default-exports {@link definePack}'s result, and `lvtt pack build` compiles it to the pack's
 * sidecar (`manifest.json` + `overlay/*.json`), which stays the runtime and distribution format
 * (`decisions/0181`). Nothing at runtime imports a `pack.ts`.
 *
 * An entry is a plain {@link OverlayEntry} literal (`export default { … } satisfies
 * OverlayEntry`), the same shape `seedEntries.ts` authors (Layer 0). The Layer-1 helpers at the
 * bottom build parts of that literal. They must return plain AST, never an opaque hook, so the
 * engine-owned fold and the coverage/lint tooling keep seeing the whole clause.
 */

import type { StatusExpiry } from "@lancer-vtt/domain";
import type {
  AddStatusAtom,
  AtomActorRef,
  ChooseAtom,
  EffectAtom,
  ForcedMoveAtom,
  IfAtom,
  ModifierContribution,
  ModifierEffect,
  OverlayEntry,
  RemoveStatusAtom,
  ResourceCap,
  TriggeredEffect,
  UseCapAtom,
} from "./atoms";
import type { AttackMatch } from "./family";
import type { CapOpenPredicate, ChosePredicate, HasStatusPredicate, Predicate, PredicateActor, StatusSourceSpec } from "./predicate";
import type { SidecarExportMeta } from "./sidecar";

/** A pack's manifest metadata: its id, the LCP it enriches, and optional name/upstreams. */
export type PackMeta = Pick<SidecarExportMeta, "id" | "enriches" | "name" | "dependsOn">;

/** What a `pack.ts` default-exports: the manifest metadata plus every entry the pack ships. */
export type PackDefinition = {
  meta: PackMeta;
  entries: readonly OverlayEntry[];
};

/**
 * Declare a pack. `entries` takes single entries or per-module arrays (a module holding both
 * ranks of a talent exports an array), flattened in order. Duplicate ids are the build's to
 * reject, where it can name both sources.
 */
export function definePack(meta: PackMeta, entries: readonly (OverlayEntry | readonly OverlayEntry[])[]): PackDefinition {
  return { meta, entries: entries.flat() as OverlayEntry[] };
}

/**
 * The `{VAL}` template placeholder, for a numeric slot whose number the tag's bound value
 * supplies at load (`bindEntry`). The slot is typed `number` (the bound shape), so the
 * placeholder string crosses this typed seam in one place: `val: VAL`.
 */
export const VAL: number = "{VAL}" as unknown as number;

// ── Layer 1: helpers that build plain AST ─────────────────────────────────────────
//
// Every helper here returns the same plain object a Layer-0 literal would spell out, so the
// sidecar, the engine's fold and the coverage/lint tooling see exactly what they saw before.
// They exist to name the repeated shapes (a choice read by later clauses, a status marker
// added in one clause and read in another, an attack-triggered rider) and to turn a
// misspelled option id into a compile error. Grow them from real duplication only.

/** The attack-family event facts, minus the `family` tag the helpers add. */
export type AttackFacts = Omit<AttackMatch, "family">;

/**
 * A choice made once and read later in the same resolution: `choose()` emits the `choose`
 * atom, `is(id)` the `chose` predicate a later clause gates on. Option ids are the keys of
 * `options` (in order; each value is the option's label), so `is("thumpr")` doesn't compile.
 */
export type Choice<Id extends string> = {
  readonly prompt: string;
  /** The `choose` atom offering every option; `effects` supplies an option's own atoms. */
  choose(effects?: Partial<Record<Id, EffectAtom[]>>, opts?: Pick<ChooseAtom, "actor">): ChooseAtom;
  /** `{ op: "chose", key: prompt, value: id }`: holds once `id` was picked this resolution. */
  is(id: Id): ChosePredicate;
};

export function choice<const Options extends Record<string, string>>(
  prompt: string,
  options: Options,
): Choice<Extract<keyof Options, string>> {
  return {
    prompt,
    choose: (effects = {}, opts = {}) => ({
      op: "choose",
      prompt,
      options: Object.entries(options).map(([id, label]) => ({
        id,
        label,
        effect: (effects as Record<string, EffectAtom[] | undefined>)[id] ?? [],
      })),
      ...opts,
    }),
    is: (id) => ({ op: "chose", key: prompt, value: id }),
  };
}

/** A named status one clause adds or removes and another reads. */
export type Status = {
  readonly name: string;
  add(target?: AtomActorRef, until?: StatusExpiry): AddStatusAtom;
  remove(target?: AtomActorRef): RemoveStatusAtom;
  /** `{ op: "has_status", status, who }`, plus `source` when only a matching instance counts. */
  isOn(who?: PredicateActor, source?: StatusSourceSpec): HasStatusPredicate;
};

export function status(name: string): Status {
  return {
    name,
    add: (target = "self", until) => ({ op: "add_status", status: name, target, ...(until !== undefined ? { until } : {}) }),
    remove: (target = "self") => ({ op: "remove_status", status: name, target }),
    isOn: (who = "self", source) => ({ op: "has_status", status: name, who, ...(source !== undefined ? { source } : {}) }),
  };
}

/**
 * A cadence cap one clause gates on and spends (`event-engine-costs` 6.1): `open` is its
 * `condition`, `use` its first atom. `on` counts it per character: "this can be used on each
 * character 1/scene" is `cap({ key, max: 1, per: "scene" }, "target")`.
 */
export function cap(spec: ResourceCap, on?: "target"): { open: CapOpenPredicate; use: UseCapAtom } {
  return {
    open: { op: "cap_open", who: "self", cap: spec, ...(on !== undefined ? { on } : {}) },
    use: { op: "use_cap", cap: spec, ...(on !== undefined ? { on } : {}) },
  };
}

/** A status that lasts until something removes it (the default an omitted `until` also means). */
export const UNTIL_REMOVED: StatusExpiry = { kind: "until_removed" };

export const and = (...preds: Predicate[]): Predicate => ({ op: "and", preds });
export const not = (pred: Predicate): Predicate => ({ op: "not", pred });

type Gate = {
  /** The state predicate (`condition`). */
  when?: Predicate;
};

/** A modifier clause. `match` gates it on attack facts (`family: "attack"` is added). */
export function modifier(mod: ModifierContribution, gate: Gate & { match?: AttackFacts } = {}): ModifierEffect {
  return {
    kind: "modifier",
    ...(gate.when !== undefined ? { condition: gate.when } : {}),
    ...(gate.match !== undefined ? { match: { family: "attack", ...gate.match } } : {}),
    mod,
  };
}

/** The `TriggeredEffect` fields a helper passes through untouched (caps, opt-in, name, …). */
type TriggerExtras = Omit<TriggeredEffect, "kind" | "on" | "match" | "condition" | "effect">;

/** A clause triggered by an attack whose facts match `facts`. */
export function onAttack(facts: AttackFacts, effect: EffectAtom[], opts: Gate & TriggerExtras = {}): TriggeredEffect {
  const { when, ...extras } = opts;
  return {
    kind: "triggered",
    on: "attack",
    match: { family: "attack", ...facts },
    ...(when !== undefined ? { condition: when } : {}),
    ...extras,
    effect,
  };
}

/** A rider on a hit: {@link onAttack} with `hit: true` plus any further facts. */
export function onHit(effect: EffectAtom[], opts: Gate & TriggerExtras & { match?: AttackFacts } = {}): TriggeredEffect {
  const { match, ...rest } = opts;
  return onAttack({ ...match, hit: true }, effect, rest);
}

/** Push `target` `dist` spaces away from `from` (KNOCKBACK's mechanism). */
export function push(dist: number, target: AtomActorRef = "target", from: AtomActorRef = "attacker"): ForcedMoveAtom {
  return { op: "forced_move", target, dist, away_from: from };
}

// ── npcs helpers ──────────────────────────────────────────────────────────────────

export const or = (...preds: Predicate[]): Predicate => ({ op: "or", preds });

function onLifecycle(on: "start_turn" | "end_turn", effect: EffectAtom[], opts: Gate & TriggerExtras): TriggeredEffect {
  const { when, ...extras } = opts;
  return { kind: "triggered", on, ...(when !== undefined ? { condition: when } : {}), ...extras, effect };
}

/** A clause triggered at the start of the carrier's own turn. */
export function onStartTurn(effect: EffectAtom[], opts: Gate & TriggerExtras = {}): TriggeredEffect {
  return onLifecycle("start_turn", effect, opts);
}

/** A clause triggered at the end of the carrier's own turn. */
export function onEndTurn(effect: EffectAtom[], opts: Gate & TriggerExtras = {}): TriggeredEffect {
  return onLifecycle("end_turn", effect, opts);
}

// ── gear helpers ──

type TurnExpiry = Extract<StatusExpiry, { kind: "turn" }>;

/**
 * Lasts until the end of `who`'s turn: `"target"` is the status's carrier ("until the end of
 * their next turn"), `"source"` whoever applied it ("until the end of your next turn").
 */
export const turnEnd = (who: TurnExpiry["who"]): TurnExpiry => ({ kind: "turn", edge: "end", who });

/** Lasts until the start of `who`'s turn (see {@link turnEnd}). */
export const turnStart = (who: TurnExpiry["who"]): TurnExpiry => ({ kind: "turn", edge: "start", who });

/**
 * "An ally may choose to accept it; a hostile target must save": `effect` lands on a non-hostile
 * target outright, and on a hostile one only when it fails a `stat` save.
 */
export function saveUnlessAlly(stat: string, effect: EffectAtom[]): IfAtom {
  return {
    op: "if",
    cond: { op: "hostile_to", who: "target", relativeTo: "self" },
    then: [{ op: "force_save", actor: "target", stat, on_fail: effect }],
    else: effect,
  };
}
